import { useAuth0 } from "@auth0/auth0-react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { UnpackResponse } from "../../../api/src/lib";
import type { AdminController } from "../../../api/src/admin/admin.controller";
import { HttpStatusError } from "./useAdminRebalanceExplain";

// One page of a firm's rebalances from the staff-guarded
// `GET /admin/rebalances` endpoint (s-firm-scoped-admin-proposal-019ea1d9 R1).
// The `rebalances` array is the SAME shape the advisor `/rebalances/filter`
// endpoint returns, so the page reuses the existing list columns; `hasMore`
// lets the page paginate (R12/AC10).
export type AdminRebalanceListPage = UnpackResponse<
  AdminController["getRebalanceList"]
>["data"];

type WrappedPage = UnpackResponse<AdminController["getRebalanceList"]>;

// Terminal load states for the admin rebalance list. After `loading` resolves,
// exactly one of these is reached (R23–R27 / AC15–AC17):
//   - `idle`:        no firm selected — 0 list requests issued (R23/AC14)
//   - `ok`:          a page loaded; an empty `rebalances` array is the
//                    empty-state (R25/AC15), distinct from `error`.
//   - `invalid-rql`: a 400 for an invalid RQL `q` — the page shows `message`
//                    and retains the entered query text (R27/AC7).
//   - `error`:       any non-400 failure (5xx, network, 404) — distinct error
//                    state with a retry affordance (R26/AC16).
export type AdminRebalanceListStatus =
  "idle" | "loading" | "ok" | "invalid-rql" | "error";

export interface AdminRebalanceListParams {
  firmId: number | undefined;
  q?: string;
  page?: number;
  limit?: number;
  /**
   * APP-2140/R60/R70: the caller's gate. `false` while the firm list read runs,
   * after it fails, or when it returns no firm — no list read is sent, also
   * for a valid `firmId`. Defaults to `true`.
   */
  enabled?: boolean;
}

export interface UseAdminRebalanceListResult {
  status: AdminRebalanceListStatus;
  page: AdminRebalanceListPage | undefined;
  /** The server's `{ message }` for an invalid-RQL 400 (R27/AC7). */
  invalidRqlMessage: string | undefined;
  /** Refetch the current page (the retry affordance, R26/AC16). */
  retry: () => void;
}

/**
 * Fetch one page of a firm's rebalances for the staff list
 * (s-firm-scoped-admin-proposal-019ea1d9). GET-only — the page issues no
 * mutating request (R22/AC12).
 *
 * Reads the raw HTTP status so a 400 (invalid RQL `q`, R27) is distinguished
 * from any other failure (R26): on a 400 it surfaces the server's `{ message }`
 * and the caller retains the entered query text; any non-400 is the generic
 * error state. When `firmId` is unset the query is disabled, so NO list request
 * is issued (R23/AC14).
 */
export function useAdminRebalanceList(
  params: AdminRebalanceListParams,
): UseAdminRebalanceListResult {
  const { getAccessTokenSilently } = useAuth0();
  const { firmId, q, page = 1, limit, enabled: callerEnabled = true } = params;
  // APP-2140/R60/R70: a valid `firmId` alone no longer enables the read.
  const enabled =
    callerEnabled && firmId != null && Number.isInteger(firmId) && firmId > 0;

  const query = useQuery<AdminRebalanceListPage, HttpStatusError>({
    queryKey: ["admin", "rebalances", "list", firmId, q ?? "", page, limit],
    enabled,
    retry: false,
    // Keep the shown page while the next one loads, but only within the same
    // firm and query: a firm or query change must not show the old rows.
    placeholderData: (previous, previousQuery) => {
      const key = previousQuery?.queryKey;
      return key && key[3] === firmId && key[4] === (q ?? "")
        ? keepPreviousData(previous)
        : undefined;
    },
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      const search = new URLSearchParams();
      search.set("firmId", String(firmId));
      search.set("page", String(page));
      if (limit != null) search.set("limit", String(limit));
      if (q && q.trim()) search.set("q", q);
      const res = await fetch(
        `${process.env.REACT_APP_API_ROOT}/admin/rebalances?${search.toString()}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );
      if (!res.ok) {
        // Recover the server's { message } so the 400 invalid-RQL state can
        // surface it (R27/AC7); fall back to a generic message otherwise.
        let message = `Request failed (${res.status})`;
        try {
          const body = (await res.json()) as { message?: unknown };
          if (typeof body.message === "string" && body.message.length > 0) {
            message = body.message;
          }
        } catch {
          // Non-JSON body — keep the generic message.
        }
        throw new HttpStatusError(res.status, message);
      }
      const body = (await res.json()) as WrappedPage;
      return body.data;
    },
  });

  if (!enabled) {
    return {
      status: "idle",
      page: undefined,
      invalidRqlMessage: undefined,
      retry: () => query.refetch(),
    };
  }
  if (query.isPending) {
    return {
      status: "loading",
      page: undefined,
      invalidRqlMessage: undefined,
      retry: () => query.refetch(),
    };
  }
  if (query.isError) {
    const isInvalidRql = query.error.status === 400;
    return {
      status: isInvalidRql ? "invalid-rql" : "error",
      page: undefined,
      invalidRqlMessage: isInvalidRql ? query.error.message : undefined,
      retry: () => query.refetch(),
    };
  }
  return {
    status: "ok",
    page: query.data,
    invalidRqlMessage: undefined,
    retry: () => query.refetch(),
  };
}
